'use client'

/**
 * useBlockedUsers — loads the caller's set of blocked user ids once (from the
 * `my_blocks` view, which is security_invoker so it returns only the viewer's
 * rows) and exposes an optimistic updater for the UgcActionsMenu's onBlockChange.
 * Client-side list components filter their rows by `!blockedIds.has(authorId)`.
 * Mirrors the mobile app's getBlockedUserIds() filter.
 */

import { useCallback, useEffect, useState } from 'react'
import { createClient } from '@/lib/supabase/client'

export function useBlockedUsers() {
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    const supabase = createClient()
    let cancelled = false
    ;(async () => {
      const { data } = await supabase.from('my_blocks').select('blocked_user_id')
      if (cancelled || !data) return
      setBlockedIds(new Set((data as { blocked_user_id: string | null }[]).map((r) => r.blocked_user_id).filter((id): id is string => !!id)))
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onBlockChange = useCallback((userId: string, blocked: boolean) => {
    setBlockedIds((prev) => {
      const next = new Set(prev)
      if (blocked) next.add(userId)
      else next.delete(userId)
      return next
    })
  }, [])

  return { blockedIds, onBlockChange }
}
